import { components as c } from "@/components";
import Autocomplete from "@/components/ui/autocomplete";
import { Label } from "@/components/ui/label";
import { createRedirectUrl } from "@/features/auth/utils/createRedirectUrl";
import { supabaseClient } from "@/lib/supabase";
import { EmailZ, RoleZ } from "@/types/zod/auth";
import { FirstNameZ, LastNameZ, TitleZ } from "@/types/zod/auth";
import type { QuickstartDataSchemaT } from "@/types/zod/quickstart-settings";
import { useAuth } from "@/utils/auth";
import { useForm, zodResolver } from "@/utils/form";
import type { SubmitHandler } from "@/utils/form";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useProfileByIdQuery } from "../../../profile/api/selectProfile";
import { useSelectPatientLikeEmailByOrganisationId } from "./selectPatientLikeEmailByOrganisationId";
// TODO - Add Hospital Number - NON OPTIONAL FOR NHS

type Title = "Mr" | "Mrs" | "Ms" | "Miss" | "Dr" | "Prof";

interface Patient {
  email: string;
  first_name: string;
  last_name: string;
  title: Title;
}

export const formSchema = z.object({
  title: TitleZ,
  first_name: FirstNameZ,
  last_name: LastNameZ,
  email: EmailZ,
  role: RoleZ,
  invited_by: z.string().uuid(),
  invited_by_formal_name: z.string().optional(),
  redirect_url: z.string().url().optional(),
  organisation_id: z.string().uuid(),
  organisation_name: z.string().optional(),
});

type FormSchemaType = z.infer<typeof formSchema>;

interface InvitePatientFormProps {
  quickstart: QuickstartDataSchemaT;
}

const InvitePatientForm = ({ quickstart }: InvitePatientFormProps) => {
  console.log(quickstart);
  const auth = useAuth();
  const { data: profile } = useProfileByIdQuery(auth.id);
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      role: "PATIENT",
      invited_by: auth.id,
      invited_by_formal_name: profile?.formal_name ?? "your Care Team",
      redirect_url: createRedirectUrl("/confirm-account"),
      organisation_id: quickstart?.organisation_id,
      organisation_name: quickstart?.organisation_name,
    },
  });

  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormSchemaType> = async (formData) => {
    const { data, error } = await supabaseClient.functions.invoke(
      "public-invite",
      {
        body: {
          ...formData,
          quickstart: quickstart,
        },
      },
    );
    if (error) {
      toast.error(error.message);
    }

    if (data) {
      toast.success("Invite Sent");
      navigate({
        to: "/new/diary/invite/complete",
      });
    }
  };

  const { data: patients } = useSelectPatientLikeEmailByOrganisationId({
    organisationId: quickstart?.organisation_id ?? "",
    email: form.watch("email") ?? "",
  }) as { data: Patient[] | undefined };

  const fetchSuggestions = async () => {
    return patients?.map((p) => p.email).filter(Boolean) ?? [];
  };

  useEffect(() => {
    const selectedPatient = patients?.find(
      (p) => p.email === form.watch("email"),
    );
    if (selectedPatient) {
      // Update both root level and quickstart
      form.setValue("first_name", selectedPatient.first_name);
      form.setValue("last_name", selectedPatient.last_name);
      form.setValue("title", selectedPatient.title);
    }
  }, [patients, form.watch]);

  return (
    <c.ui.Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 text-lg"
      >
        <div className="flex flex-col gap-2">
          <Label>Email</Label>
          <Autocomplete
            fetchSuggestions={fetchSuggestions}
            options={patients?.map((e) => e.email) ?? []}
            value={form.watch("email")}
            onChange={(value) => {
              form.setValue("email", value);
            }}
            placeholder="Search patients by email"
          />
        </div>
        <c.fc.SelectTitle />
        <c.fc.FirstName name="first_name" />
        <c.fc.LastName name="last_name" />
        <br />
        <div className="flex flex-col gap-y-2">
          <c.ui.Button
            onClick={() => console.log(form.getValues(), form.formState.errors)}
            disabled={form.formState.isSubmitting}
            size="lg"
            type="submit"
          >
            Next
          </c.ui.Button>
        </div>
      </form>
    </c.ui.Form>
  );
};

export default InvitePatientForm;
